import { useEffect, useState, useCallback } from "react";
import * as Scrivito from "scrivito";
import * as Scroll from "react-scroll";

import classNames from "classnames";
import I18n from "^/config/i18n";
import setLanguage from "^/utils/setLanguage";
import PageHeadBlock from "^/Components/StoriesPreview/PageHeadBlock";
import SearchInput from "./SearchInput";
import SearchResultItem from "./SearchResultItem";

const blacklistObjClasses = [
  "Image",
  "SearchResults",
  "Video",
  "LocalsConfiObj",
  "LoginPage",
  "Quote",
  "Root",
];
const pageCategories = ["pages", "all", "documents", "people"];
const hitsPerPage = 10;

function SearchResultsComponent({ page }) {
  setLanguage(page);
  const [count, setCount] = useState({
    all: 0,
    documents: 0,
    pages: 0,
    people: 0,
  });
  const [currentPageNumber, setCurrentPageNumber] = useState(0);
  const [hits, setHits] = useState([]);
  const [fetching, setFetching] = useState(true);
  const { q, filter } = Scrivito.currentPageParams();
  const [query, setQuery] = useState(q || "");

  useEffect(() => {
    if (q !== query) {
      setQuery(q || "");
    }
  }, [q, query]);

  const uniqueSearchResultHits = (searchHits) =>
    searchHits.filter(
      (obj, index, self) =>
        obj.objClass() !== "ContactPage" ||
        index === self.findIndex((o) => o.get("email") === obj.get("email"))
    );

  const addFilter = (searchQuery, pageType) => {
    if (pageType === "all") {
      return searchQuery;
    }

    const classMap = {
      pages: ["Page", "CoursePage"],
      documents: "Download",
      people: "ContactPage",
    };
    return searchQuery.and("_objClass", "equals", classMap[pageType]);
  };

  const basicSearch = (que, siteId) => {
    const currentHour = new Date();
    currentHour.setMinutes(0, 0, 0);
    return Scrivito.Obj.onSite(siteId)
      .all()
      .andFullTextOf("*", "matches", que || "")
      .andNot(["publishFromDate", "validFrom"], "isGreaterThan", currentHour)
      .andNot(
        ["publishUntilDate", "validUntil", "endDate"],
        "isLessThan",
        currentHour
      )
      .andNot("_objClass", "equals", blacklistObjClasses)
      .boost("title", "containsPrefix", que || "", 10)
      .boost("title", "startsWith", que || "", 5);
  };

  const fetchResults = useCallback(
    (pageNumber) => {
      if (!pageNumber || pageNumber === 0) {
        setCurrentPageNumber(0);
        setHits([]);
      }
      setFetching(true);
      Scrivito.load(() => {
        const result = { count: {} };
        const siteId = page.siteId();
        result.count.all = basicSearch(query, siteId).count();
        result.hits = addFilter(basicSearch(query, siteId), filter || "pages")
          .offset(hitsPerPage * pageNumber)
          .take(hitsPerPage);
        result.count.pages = addFilter(
          basicSearch(query, siteId),
          "pages"
        ).count();
        result.count.documents = addFilter(
          basicSearch(query, siteId),
          "documents"
        ).count();
        result.count.people = addFilter(
          basicSearch(query, siteId),
          "people"
        ).count();

        // if on english site, also german ContactPages should be visible
        // because not all english versions are created yet
        if (siteId === "en") {
          result.additionalPeopleCount = addFilter(
            basicSearch(query, "de"),
            "people"
          ).count();

          result.additionalPeopleHits = addFilter(
            basicSearch(query, "de"),
            "people"
          )
            .offset(hitsPerPage * pageNumber)
            .take(hitsPerPage);
        } else {
          result.additionalPeopleCount = 0;
          result.additionalPeopleHits = [];
        }

        return result;
      }).then((result) => {
        if (pageNumber === 0) {
          setHits(
            uniqueSearchResultHits([
              ...result.hits,
              ...result.additionalPeopleHits,
            ])
          );
        } else {
          setHits((prevHits) => [
            ...prevHits,
            ...result.hits,
            ...result.additionalPeopleHits,
          ]);
        }
        result.count.people += result.additionalPeopleCount;
        result.count.all += result.additionalPeopleCount;
        setCount(result.count);
        setFetching(false);
      });
    },
    [query, filter, page]
  );

  useEffect(() => {
    fetchResults(currentPageNumber);
  }, [query, filter, currentPageNumber, fetchResults]);

  return (
    <div>
      <PageHeadBlock page={page} renderBreadcrumbs={true} />
      <Scroll.Element id="afterHero" />
      <section className="bg-lighter-gray s-py-3 --large">
        <div className="container">
          <SearchInput q={query} page={page} />
          {count && query && (
            <h1
              className="py-3 color-dark h4"
              dangerouslySetInnerHTML={{
                __html: I18n.t("searchResultText", {
                  query,
                  count: count.all,
                }),
              }}
            />
          )}
          <div className="search-filter row">
            {count &&
              pageCategories.map((category) => (
                <button
                  key={category}
                  onClick={() => {
                    setCurrentPageNumber(0);
                    Scrivito.navigateTo(page, {
                      params: { q, filter: category },
                    });
                  }}
                  className={classNames("btn col-sm", {
                    "bg-blue": (filter || "pages") === category,
                    "bg-light-gray": (filter || "pages") !== category,
                  })}
                >
                  <span>
                    {I18n.t(`search.filterCount.${category}`, {
                      count: count[category],
                    })}
                  </span>
                </button>
              ))}
          </div>
        </div>
      </section>

      <section className="bg-white mt-4">
        <div className="container">
          {fetching && (
            <div className="la-4x text-center pt-4 mt-4 mb-4 pb-4">
              <i className="las la-spinner la-spin" aria-hidden="true" />
            </div>
          )}
          {hits &&
            hits.map((resultItem, index) => (
              <SearchResultItem resultItem={resultItem} key={index} />
            ))}
          {count[filter || "all"] > hits.length && (
            <div className="col-md-6 offset-md-3 pb-4 mb-4">
              <div className="col text-center">
                <button
                  className="btn bg-blue"
                  onClick={() => setCurrentPageNumber(currentPageNumber + 1)}
                >
                  {fetching && (
                    <i
                      className="las la-spinner la-spin me-2"
                      aria-hidden="true"
                    />
                  )}
                  {fetching && (
                    <span className="sr-only">{I18n.t("loading")}</span>
                  )}
                  {I18n.t("search.loadMore")}
                </button>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

Scrivito.provideComponent("SearchResults", SearchResultsComponent);
